import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchForumAdmin, moderate, type ForumAdminResponse } from "../api/forumApi";
import { formatRelativeTime } from "../utils/time";
import { showError, confirmDanger } from "./Dialog";

/**
 * Forum moderation, embedded in the /admin page. Everything here calls
 * netlify/functions/forum-moderate.ts, which re-checks the admin account
 * server-side — this panel being hidden is a convenience, not the
 * control. (And that server check is a name match, not a signature; see
 * that file's header for exactly how much to trust it.)
 *
 * Grouped the way moderation actually happens: what needs attention
 * (reports) first, then the blunt instruments (bans, board lock), then
 * housekeeping (categories, thread list).
 */
export function ForumAdminPanel({ account }: { account: string }) {
  const [data, setData] = useState<ForumAdminResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [banName, setBanName] = useState("");
  const [banIp, setBanIp] = useState("");
  const [banWord, setBanWord] = useState("");
  const [purgeName, setPurgeName] = useState("");
  const [newCatId, setNewCatId] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatDesc, setNewCatDesc] = useState("");
  const [threadFilter, setThreadFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      setData(await fetchForumAdmin(account));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load forum moderation data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  async function run(action: string, payload: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      // Category edits need the current list sent along the first time,
      // because until an admin changes something the board is still
      // running on forum-read.ts's built-in defaults with no "settings"
      // blob written yet.
      const withCats = action.endsWith("-category") ? { categories: data?.settings.categories, ...payload } : payload;
      await moderate(action, withCats, account);
      await load();
    } catch (err) {
      showError(err, "That action failed.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="state-message">Loading forum moderation…</div>;
  if (error) return <div className="state-message state-message--error">{error}</div>;
  if (!data) return null;

  const openReports = data.reports.filter((r) => !r.resolved);
  const threads = data.threads
    .filter((t) => !threadFilter || t.title.toLowerCase().includes(threadFilter.toLowerCase()))
    .slice()
    .sort((a, b) => (b.lastActivityAt || b.createdAt).localeCompare(a.lastActivityAt || a.createdAt));

  return (
    <div className="admin-section">
      <h2>Forum moderation</h2>
      <p className="admin-section__subtitle">
        {data.threads.length} threads · {openReports.length} open report{openReports.length === 1 ? "" : "s"} ·{" "}
        {data.bans.names.length + data.bans.ipHashes.length} bans · {data.bans.words.length} blocked words
      </p>

      <div className="forum-admin-grid">
        {/* ---- board controls ---- */}
        <div className="forum-admin-card">
          <h3>Board controls</h3>
          <label className="forum-admin-toggle">
            <input
              type="checkbox"
              checked={data.settings.forumLocked}
              disabled={busy}
              onChange={(e) => run("set-forum-lock", { value: e.target.checked })}
            />
            <span>Read-only mode (nobody can post)</span>
          </label>
          <label className="forum-admin-toggle">
            <input
              type="checkbox"
              checked={data.settings.requireWallet}
              disabled={busy}
              onChange={(e) => run("set-require-wallet", { value: e.target.checked })}
            />
            <span>Require a connected WAX wallet to post</span>
          </label>
          <p className="forum-admin-note">
            Read-only is the kill switch for a spam wave; wallet-only is the slower brake that keeps the board open
            to anyone who has a WAX account.
          </p>
        </div>

        {/* ---- reports ---- */}
        <div className="forum-admin-card">
          <h3>Reports</h3>
          {data.reports.length === 0 && <p className="forum-admin-note">Nothing reported.</p>}
          {data.reports.slice(0, 15).map((r) => (
            <div key={r.id} className={`forum-admin-report${r.resolved ? " forum-admin-report--done" : ""}`}>
              <div>
                <Link to={`/forum/t/${r.threadId}`} className="forum-text__link">
                  thread {r.threadId.slice(-6)}
                </Link>{" "}
                · post {r.postId.slice(-6)} · by {r.reporter}{" "}
                <span className="forum-post__time">{formatRelativeTime(new Date(r.createdAt).getTime())}</span>
              </div>
              <div className="forum-admin-report__reason">“{r.reason}”</div>
              <div className="forum-admin-report__actions">
                {!r.resolved && (
                  <button className="link-button" disabled={busy} onClick={() => run("resolve-report", { id: r.id })}>
                    Mark handled
                  </button>
                )}
                <button
                  className="link-button"
                  disabled={busy}
                  onClick={() => run("delete-post", { threadId: r.threadId, postId: r.postId })}
                >
                  Remove the post
                </button>
                <button className="link-button forum-post__danger" disabled={busy} onClick={() => run("delete-report", { id: r.id })}>
                  Dismiss
                </button>
              </div>
            </div>
          ))}
          {data.reports.length > 0 && (
            <button className="btn btn--outline btn--small" disabled={busy} onClick={() => run("clear-reports")}>
              Clear all reports
            </button>
          )}
        </div>

        {/* ---- bans ---- */}
        <div className="forum-admin-card">
          <h3>Bans & filters</h3>
          <div className="forum-admin-row">
            <input placeholder="Display name to ban" value={banName} onChange={(e) => setBanName(e.target.value)} />
            <button
              className="btn btn--small btn--outline"
              disabled={busy || !banName.trim()}
              onClick={() => {
                run("ban-name", { value: banName.trim() });
                setBanName("");
              }}
            >
              Ban
            </button>
          </div>
          <div className="forum-admin-chips">
            {data.bans.names.map((n) => (
              <button key={n} className="forum-chip" disabled={busy} onClick={() => run("unban-name", { value: n })}>
                {n} ✕
              </button>
            ))}
          </div>

          <div className="forum-admin-row">
            <input placeholder="IP hash to ban" value={banIp} onChange={(e) => setBanIp(e.target.value)} />
            <button
              className="btn btn--small btn--outline"
              disabled={busy || !banIp.trim()}
              onClick={() => {
                run("ban-ip", { value: banIp.trim() });
                setBanIp("");
              }}
            >
              Ban
            </button>
          </div>
          <div className="forum-admin-chips">
            {data.bans.ipHashes.map((h) => (
              <button key={h} className="forum-chip" disabled={busy} onClick={() => run("unban-ip", { value: h })}>
                {h} ✕
              </button>
            ))}
          </div>
          <p className="forum-admin-note">
            IP hashes are salted SHA-256, shown on each post's mod row — not reversible to an address.
          </p>

          <div className="forum-admin-row">
            <input placeholder="Blocked word/phrase" value={banWord} onChange={(e) => setBanWord(e.target.value)} />
            <button
              className="btn btn--small btn--outline"
              disabled={busy || !banWord.trim()}
              onClick={() => {
                run("add-word", { value: banWord.trim() });
                setBanWord("");
              }}
            >
              Block
            </button>
          </div>
          <div className="forum-admin-chips">
            {data.bans.words.map((w) => (
              <button key={w} className="forum-chip" disabled={busy} onClick={() => run("remove-word", { value: w })}>
                {w} ✕
              </button>
            ))}
          </div>

          <div className="forum-admin-row">
            <input placeholder="Purge everything by name" value={purgeName} onChange={(e) => setPurgeName(e.target.value)} />
            <button
              className="btn btn--small btn--outline forum-post__danger"
              disabled={busy || !purgeName.trim()}
              onClick={async () => {
                if (await confirmDanger(`Remove every post and thread by "${purgeName}"?`)) {
                  run("purge-author", { name: purgeName.trim() });
                  setPurgeName("");
                }
              }}
            >
              Purge
            </button>
          </div>
        </div>

        {/* ---- categories ---- */}
        <div className="forum-admin-card">
          <h3>Categories</h3>
          {data.settings.categories.map((c) => (
            <div key={c.id} className="forum-admin-row">
              <input
                defaultValue={c.name}
                onBlur={(e) => {
                  if (e.target.value.trim() && e.target.value !== c.name) {
                    run("update-category", { id: c.id, name: e.target.value.trim(), description: c.description });
                  }
                }}
              />
              <button
                className="btn btn--small btn--outline forum-post__danger"
                disabled={busy}
                onClick={async () => {
                  if (await confirmDanger(`Remove "${c.name}"? Its threads move to the first category.`)) {
                    run("remove-category", { id: c.id });
                  }
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <div className="forum-admin-row forum-admin-row--stack">
            <input placeholder="new-category-id" value={newCatId} onChange={(e) => setNewCatId(e.target.value)} />
            <input placeholder="Display name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} />
            <input placeholder="Description" value={newCatDesc} onChange={(e) => setNewCatDesc(e.target.value)} />
            <button
              className="btn btn--small btn--primary"
              disabled={busy || !newCatId.trim() || !newCatName.trim()}
              onClick={() => {
                run("add-category", { id: newCatId.trim(), name: newCatName.trim(), description: newCatDesc.trim() });
                setNewCatId("");
                setNewCatName("");
                setNewCatDesc("");
              }}
            >
              Add category
            </button>
          </div>
        </div>
      </div>

      {/* ---- all threads ---- */}
      <div className="forum-admin-card" style={{ marginTop: 16 }}>
        <h3>All threads</h3>
        <input
          placeholder="Filter by title…"
          value={threadFilter}
          onChange={(e) => setThreadFilter(e.target.value)}
          style={{ marginBottom: 10, width: "100%" }}
        />
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Thread</th>
                <th>Author</th>
                <th>Replies</th>
                <th>Last activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {threads.slice(0, 100).map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/forum/t/${t.id}`} className="forum-text__link">
                      {t.title}
                    </Link>
                    {t.pinned && <span className="forum-tag forum-tag--pin">Pinned</span>}
                    {t.locked && <span className="forum-tag">Locked</span>}
                    {t.hidden && <span className="forum-tag forum-tag--hidden">Hidden</span>}
                  </td>
                  <td>{t.author || "anon"}</td>
                  <td>{t.replyCount}</td>
                  <td>{formatRelativeTime(new Date(t.lastActivityAt || t.createdAt).getTime())}</td>
                  <td className="forum-admin-actions">
                    <button className="link-button" disabled={busy} onClick={() => run(t.pinned ? "unpin" : "pin", { threadId: t.id })}>
                      {t.pinned ? "Unpin" : "Pin"}
                    </button>
                    <button className="link-button" disabled={busy} onClick={() => run(t.locked ? "unlock" : "lock", { threadId: t.id })}>
                      {t.locked ? "Unlock" : "Lock"}
                    </button>
                    <button className="link-button" disabled={busy} onClick={() => run(t.hidden ? "unhide" : "hide", { threadId: t.id })}>
                      {t.hidden ? "Unhide" : "Hide"}
                    </button>
                    <button
                      className="link-button forum-post__danger"
                      disabled={busy}
                      onClick={async () => {
                        if (await confirmDanger(`Delete "${t.title}" permanently?`)) run("delete-thread", { threadId: t.id });
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
